import { useState, useEffect } from "react";

interface ImageUploadProps {
    label: string;
    value: string;
    onChange: (base64: string) => void;
    accept?: string;
}

// La imagen viaja como data URL dentro del JSON del formulario, y el back corta
// cualquier body de más de 1 MB (app.js) con un 413. Un logo de móvil o de
// diseñador pesa fácilmente varios MB, así que se reduce aquí antes de enviarla.
// 400 KB de data URL dejan margen para el resto del formulario.
const MAX_DATA_URL = 400 * 1024;
// Lados máximos a probar, de mayor a menor: un logo se pinta como mucho a
// ~180 px de ancho (correos, cabecera), así que 512 sobra.
const LADOS = [512, 384, 256];

const leerComoDataUrl = (file: File) =>
    new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });

const cargarImagen = (src: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('No se pudo leer la imagen'));
        img.src = src;
    });

/**
 * Devuelve la imagen como data URL lista para guardar, o null si ni reducida
 * cabe. Si ya es pequeña se deja tal cual, para no perder calidad. Se reescala
 * a PNG para conservar la transparencia, que es lo normal en un logo.
 */
const prepararImagen = async (file: File): Promise<string | null> => {
    const original = await leerComoDataUrl(file);
    const img = await cargarImagen(original);
    const ladoMayor = Math.max(img.naturalWidth, img.naturalHeight);

    if (original.length <= MAX_DATA_URL && ladoMayor <= LADOS[0]) return original;
    // Un SVG sin dimensiones propias no se puede rasterizar con fiabilidad.
    if (file.type === 'image/svg+xml' || ladoMayor === 0) {
        return original.length <= MAX_DATA_URL ? original : null;
    }

    for (const lado of LADOS) {
        const escala = Math.min(1, lado / ladoMayor);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.naturalWidth * escala);
        canvas.height = Math.round(img.naturalHeight * escala);
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const reducida = canvas.toDataURL('image/png');
        if (reducida.length <= MAX_DATA_URL) return reducida;
    }
    return null;
};

export const ImageUpload = ({ label, value, onChange, accept = "image/*" }: ImageUploadProps) => {
    const [preview, setPreview] = useState<string | null>(value || null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setPreview(value || null);
    }, [value]);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target;
        const file = input.files?.[0];
        if (!file) return;
        setError(null);
        try {
            const base64 = await prepararImagen(file);
            if (!base64) {
                setError('La imagen es demasiado pesada incluso reducida. Prueba con un PNG o JPG más sencillo.');
                input.value = '';
                return;
            }
            setPreview(base64);
            onChange(base64);
        } catch {
            setError('No se pudo leer la imagen. Prueba con un PNG o JPG.');
            input.value = '';
        }
    };

    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            <div className="flex items-start gap-3">
                <div className="flex-1">
                    <input
                        type="file"
                        accept={accept}
                        onChange={handleFileChange}
                        className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
                </div>
                {preview && (
                    <div className="hidden sm:flex items-center justify-center w-24 h-24 border rounded bg-gray-50 flex-shrink-0">
                        <img
                            src={preview}
                            alt="Preview"
                            className="max-w-full max-h-full object-contain"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
